import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Form, Dropdown } from 'react-bootstrap';

import { ErrorIcon } from 'utils/icons';
import 'components/FormInput/styles.scss';
import './styles.scss';

const TypeAheadInput = React.forwardRef(
  (
    {
      options,
      onChange,
      onInputEdit,
      onBlur,
      error,
      label,
      placeholder,
      inputClassName,
      labelClassName,
      defaultValue,
      strictSelect,
      ...rest
    },
    ref
  ) => {
    const [inputValue, setInputValue] = useState(defaultValue);
    const [openMenu, setOpenMenu] = useState(false);
    const [optionsList, setOptionsList] = useState([]);
    const [selectedOption, setSelectedOption] = useState(defaultValue);

    useEffect(() => {
      setInputValue(defaultValue);
    }, [defaultValue]);

    useEffect(() => {
      setOpenMenu(false);
      if (options?.length && inputValue !== defaultValue) {
        const filteredOptions =
          options?.filter(
            item =>
              inputValue &&
              selectedOption?.toLowerCase() !== inputValue?.toLowerCase() &&
              item.label?.toLowerCase()?.startsWith(inputValue?.toLowerCase())
          ) || [];
        setOptionsList(filteredOptions);
        setOpenMenu(filteredOptions?.length ? true : false);
      }
    }, [inputValue, options, selectedOption, defaultValue]);

    const handleInputChange = event => {
      const newValue = event?.target?.value;
      setInputValue(newValue);
      onInputEdit?.(event);
      if (!newValue) {
        setSelectedOption(null);
      }
    };

    const handleKeyDown = event => {
      switch (event.key) {
        case 'Enter':
          onChange(inputValue);
          setOpenMenu(false);
          event.preventDefault();
          break;
        case 'Escape':
          onChange(inputValue);
          setOpenMenu(false);
          event.preventDefault();
          break;
        case 'Tab': {
          const newValue = optionsList?.[0]?.label
            ?.toLowerCase()
            ?.includes(inputValue.toLowerCase())
            ? optionsList?.[0]?.label
            : '';

          setInputValue(newValue || inputValue);
          newValue && setSelectedOption(newValue);
          setOpenMenu(false);
          break;
        }
        default:
          return '';
      }
    };

    const handleOnChange = itemValue => {
      const selectedItem = strictSelect
        ? options?.find(option => String(option.value) === String(itemValue))
        : null;
      const nextValue = strictSelect
        ? selectedItem?.value ?? itemValue
        : itemValue;
      setSelectedOption(nextValue);
      setInputValue(nextValue);
      if (strictSelect) {
        onChange(nextValue);
        setOpenMenu(false);
      }
    };

    const handleBlur = e => {
      onChange(inputValue);
      onBlur?.(e);
      setOpenMenu(false);
    };

    const selectedStrictOption = options?.find(
      item => String(item.value) === String(inputValue)
    );

    return (
      <Form.Group className="form-input-group">
        {label && (
          <Form.Label
            className={`d-flex align-item-start w-100 form-select-group__label ${labelClassName}`}
          >
            {label}
          </Form.Label>
        )}
        <Dropdown
          className={`type-ahead-dropdown-menu ${
            strictSelect ? 'type-ahead-dropdown-menu--strict' : ''
          }`}
          onSelect={handleOnChange}
          show={openMenu}
        >
          <Form.Control
            type="text"
            placeholder={placeholder}
            isInvalid={Boolean(error)}
            onKeyDown={strictSelect ? undefined : handleKeyDown}
            onChange={strictSelect ? undefined : handleInputChange}
            ref={ref}
            className={`form-input-group__input ${
              strictSelect ? 'form-input-group__input--select' : ''
            } ${inputClassName}`}
            autoComplete="off"
            value={
              strictSelect ? selectedStrictOption?.label || '' : inputValue
            }
            {...rest}
            readOnly={strictSelect || rest.readOnly}
            onBlur={handleBlur}
            onClick={() => strictSelect && !rest.readOnly && setOpenMenu(true)}
            onFocus={() => strictSelect && !rest.readOnly && setOpenMenu(true)}
          />
          <Dropdown.Menu
            className="type-ahead-dropdown-menu"
            onMouseDown={
              strictSelect ? event => event.preventDefault() : undefined
            }
          >
            {(strictSelect ? options : optionsList)?.map(item => (
              <Dropdown.Item
                key={item?.value}
                eventKey={strictSelect ? item.value : item.label}
              >
                {item.label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
        {error && (
          <Form.Control.Feedback type="invalid">
            <span className="error-icon">
              <ErrorIcon />
            </span>
            {error}
          </Form.Control.Feedback>
        )}
      </Form.Group>
    );
  }
);

TypeAheadInput.displayName = 'TypeAheadInput';

TypeAheadInput.propTypes = {
  onChange: PropTypes.func.isRequired,
  onInputEdit: PropTypes.func,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ).isRequired,
  labelClassName: PropTypes.string,
  inputClassName: PropTypes.string,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.string,
  onBlur: PropTypes.func,
  strictSelect: PropTypes.bool,
};

TypeAheadInput.defaultProps = {
  error: '',
  labelClassName: '',
  inputClassName: '',
  placeholder: '',
  label: '',
  defaultValue: '',
  onInputEdit: null,
  onBlur: () => null,
  strictSelect: false,
};

export default TypeAheadInput;
